<template>
  <transition name="kpi-drawer">
    <section
      v-if="visible"
      class="kpi-drawer"
      role="dialog"
      aria-modal="false"
      :aria-label="tituloAcessivel"
    >
      <header class="kpi-drawer__head">
        <div class="kpi-drawer__identity">
          <span class="kpi-drawer__bucket">{{ rotuloBucket }}</span>
          <h5 class="kpi-drawer__name">
            {{ consultantName }}
          </h5>
          <span class="kpi-drawer__period">{{ periodoLegivel }}</span>
        </div>

        <!-- Quando a soma dos itens fecha com a celula, a gaveta mostra UM
             numero com o selo "confere", e nao os dois lado a lado: dois valores
             iguais pediam para ser comparados a olho, e o selo ja diz o
             resultado da comparacao. Quando NAO fecham, os dois voltam, junto
             da tarja com a diferenca. -->
        <div class="kpi-drawer__figures">
          <div class="kpi-drawer__figure">
            <span class="kpi-drawer__figure-label">
              {{ networkAmount === null ? 'Valor da célula' : 'Produção própria' }}
            </span>
            <strong class="kpi-drawer__figure-value">
              {{ valorDaCelula }}
              <span
                v-if="meta.reconciles === true"
                class="kpi-drawer__ok"
              >
                <feather-icon
                  icon="CheckIcon"
                  size="12"
                />
                confere
              </span>
            </strong>
          </div>
          <div
            v-if="networkAmount !== null"
            class="kpi-drawer__figure"
          >
            <span class="kpi-drawer__figure-label">
              {{ columnLabel ? `${columnLabel} na tabela (com a rede)` : 'Célula na tabela (com a rede)' }}
              <feather-icon
                v-b-tooltip.hover
                icon="InfoIcon"
                size="12"
                title="O detalhamento existe por consultor: lista os contratos de uma pessoa, nunca de uma subárvore inteira."
              />
            </span>
            <strong class="kpi-drawer__figure--muted">{{ valorDaRede }}</strong>
          </div>
          <!-- Na coluna de CONTAGEM o outro lado da conta é "Itens listados",
               logo abaixo; a soma em reais seria outra grandeza. -->
          <div
            v-if="meta.reconciles === false && meta.unidade !== 'contagem'"
            class="kpi-drawer__figure"
          >
            <span class="kpi-drawer__figure-label">Soma do que está listado</span>
            <strong>{{ meta.items_amount | toCurrency }}</strong>
          </div>
          <!-- A CONTAGEM, para quem clicou numa coluna de contagem ter com o
               que conferir. Sem ela, clicar em "7 EP" abria uma gaveta que so
               falava em reais e nao dava como fechar o 7. -->
          <div
            v-if="meta.total !== undefined"
            class="kpi-drawer__figure"
          >
            <span class="kpi-drawer__figure-label">Itens listados</span>
            <strong>{{ meta.total }}</strong>
          </div>
          <div
            v-if="ltv !== null"
            class="kpi-drawer__figure"
          >
            <span class="kpi-drawer__figure-label">
              LTV/CL
              <feather-icon
                v-b-tooltip.hover
                icon="InfoIcon"
                size="12"
                title="Agregado da linha: faturamento total dividido pelos clientes distintos com contrato no período."
              />
            </span>
            <strong>{{ ltv | toCurrency }}</strong>
          </div>
        </div>

        <b-button
          variant="flat-secondary"
          size="sm"
          class="kpi-drawer__close"
          aria-label="Fechar detalhamento"
          @click="$emit('close')"
        >
          <feather-icon icon="XIcon" />
        </b-button>
      </header>

      <div
        v-if="meta.reconciles === false"
        class="kpi-drawer__warning"
      >
        <feather-icon
          icon="AlertTriangleIcon"
          size="14"
          class="mr-50"
        />
        <template v-if="meta.unidade === 'contagem'">
          Os itens listados não fecham com a célula: {{ meta.total }}
          {{ meta.total === 1 ? 'item' : 'itens' }} para uma contagem de {{ valorDaCelula }}.
        </template>
        <template v-else>
          A soma dos itens não fecha com a célula
          ({{ diferenca | toCurrency }} de diferença).
        </template>
        O detalhamento está mostrando a diferença em vez de escondê-la.
      </div>

      <div class="kpi-drawer__body">
        <futuro-loader
          v-if="loading"
          block
          size="72"
          label="Carregando detalhamento..."
        />

        <p
          v-else-if="erro"
          class="kpi-drawer__empty"
        >
          {{ erro }}
        </p>

        <p
          v-else-if="!items.length"
          class="kpi-drawer__empty"
        >
          Nenhum contrato ou pagamento compõe esta célula no período.
        </p>

        <table
          v-else
          class="kpi-drawer__table"
        >
          <thead>
            <tr>
              <th>Cliente e solução</th>
              <th v-if="mostraDono">
                Dono
                <feather-icon
                  v-b-tooltip.hover
                  icon="InfoIcon"
                  size="12"
                  title="A produção é do dono. O especialista executou, e é isso que esta leitura mede."
                />
              </th>
              <th>{{ meta.source === 'payments' ? 'Conciliado em' : 'Competência' }}</th>
              <th v-if="meta.source === 'contracts'">
                Status
              </th>
              <th class="text-right">
                Valor
                <feather-icon
                  v-b-tooltip.hover
                  icon="InfoIcon"
                  size="12"
                  title="O subtotal do cliente soma o que está listado nesta página, e não o histórico dele."
                />
              </th>
              <th class="text-right">
                Contrato
              </th>
            </tr>
          </thead>
          <tbody>
            <!--
              Duas linhas: o cliente, e abaixo dele as soluções que formam o
              valor dele. A lista plana repetia o nome uma vez por solução, e
              num detalhamento com dois ou três produtos por pessoa isso vira
              uma coluna de nomes repetidos que esconde o que é novo.

              Cliente e solução dividem a MESMA coluna, como uma árvore. Em
              colunas separadas, a de cliente ficava vazia em toda linha de
              solução, e numa gaveta da largura da tela o olho atravessava um
              vão enorme entre o nome e o que ele comprou.
            -->
            <template v-for="grupo in gruposDeCliente">
              <tr
                :key="`cli:${grupo.customerId}`"
                class="kpi-drawer__client"
              >
                <td>
                  <a
                    href="#"
                    @click.prevent="abrirCliente(grupo.customerId)"
                  >{{ nomeDoCliente(grupo.customerId) }}</a>
                  <span class="kpi-drawer__count">{{ grupo.quantos }} {{ grupo.quantos === 1 ? 'solução' : 'soluções' }}</span>
                </td>
                <td v-if="mostraDono">
                  {{ nomeDoConsultor(grupo.itens[0].owner_consultant_id) }}
                </td>
                <td :colspan="colunasDoMeio" />
                <td class="text-right kpi-drawer__value">
                  {{ grupo.total | toCurrency }}
                </td>
                <td />
              </tr>
              <tr
                v-for="item in grupo.itens"
                :key="`sol:${item.kind}:${item.payment_id || item.contract_id}`"
                class="kpi-drawer__solution"
              >
                <td>
                  <!--
                    Falta aqui o chip "Executado por" do protótipo. Ele depende
                    de `specialist_consultant_id` no item, que o
                    /contract_detail ainda não devolve — sairia do
                    ContractsAuditQuery. UI que nunca renderiza é pior do que
                    UI ausente, então fica de fora até o campo existir.
                  -->
                  <span class="kpi-drawer__sol-name">{{ item.financial_solution_name || '—' }}</span>
                </td>
                <td v-if="mostraDono" />
                <td>{{ item.reference_date | onlyDate }}</td>
                <td v-if="meta.source === 'contracts'">
                  {{ item.status_name || '—' }}
                </td>
                <td class="text-right kpi-drawer__value">
                  {{ item.value | toCurrency }}
                </td>
                <td class="text-right">
                  <a
                    href="#"
                    @click.prevent="abrirContrato(item.contract_id)"
                  >#{{ item.contract_id }}</a>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <footer
        v-if="meta.last_page > 1"
        class="kpi-drawer__foot"
      >
        <b-pagination
          :value="meta.page"
          :total-rows="meta.total"
          :per-page="meta.per_page"
          size="sm"
          class="mb-0"
          @change="$emit('page', $event)"
        />
        <span class="kpi-drawer__count">{{ meta.total }} itens</span>
      </footer>
    </section>
  </transition>
</template>

<script>
import { BButton, BPagination, VBTooltip } from 'bootstrap-vue'
import axios from 'axios'
import FuturoLoader from '@/modules/shared/components/FuturoLoader.vue'
import { groupByCustomer } from '../utils/kpiDetail'

/**
 * A gaveta de detalhamento, ancorada no fundo da tela.
 *
 * O QUE ELA PROVA
 *
 * "R$ 48.300" numa célula é um número em que se acredita ou não. A gaveta lista
 * o que o compõe e mostra os dois lados da conta lado a lado — valor da célula e
 * soma do que está listado. Quando não fecham, ela diz a diferença em vez de
 * esconder: uma divergência visível é um bug reportável, e uma escondida é
 * desconfiança permanente na tela inteira.
 *
 * A FONTE MUDA POR BUCKET
 *
 * EP e Soluções somam `contracts.gross_value`; assinatura, renovação, suporte e
 * acompanhamento vêm de pagamento conciliado. O backend já resolve isso e diz
 * em `meta.source` de onde veio — a tabela troca de colunas conforme, porque
 * "status do contrato" não existe numa linha de pagamento.
 *
 * NOME DO CLIENTE
 *
 * Não vem do KPI: a réplica não guarda PII. Vem de GET /api/customers/names,
 * em uma chamada para a página inteira, e fica num cache de módulo para virar
 * página e voltar não repetir a busca. Enquanto não chega, a linha mostra o id
 * — a gaveta aparece com valor e contrato na hora, e o nome preenche depois.
 *
 * LTV É AGREGADO
 *
 * Aparece uma vez no cabeçalho, marcado. É da linha inteira, e não de um
 * cliente ou de uma solução: faturamento total ÷ clientes distintos com
 * contrato no período (a regra do card 1217381846456640).
 */

/** Vive além do componente de propósito: fechar e reabrir a gaveta não refaz a busca. */
const nomesConhecidos = new Map()

const ROTULOS_DE_BUCKET = {
  ep: 'Entrevistas pagas',
  other_solution: 'Outras soluções financeiras',
  subscription: 'Assinaturas',
  signature: 'Acompanhamentos',
  support: 'Suportes',
  renewal: 'Renovações',
}

export default {
  name: 'KpiDetailDrawer',
  components: { BButton, BPagination, FuturoLoader },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    erro: {
      type: String,
      default: null,
    },
    consultantName: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    meta: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Nome por id de consultor, para rotular o dono.
     *
     * Vem pronto da tela, que já carrega a lista de consultores para o filtro
     * de PN — buscar de novo aqui seria uma chamada a mais para um dado que já
     * está em memória.
     */
    consultantNames: {
      type: Object,
      default: () => ({}),
    },
    /** Agregado da linha, calculado pela coluna LTV/CL. */
    ltv: {
      type: Number,
      default: null,
    },
    /**
     * Valor que a célula clicada mostra quando a tabela está na leitura "Rede"
     * e o consultor tem equipe. Null quando os dois números são o mesmo.
     *
     * Sem isto, "Produção própria: R$ 30 mil" apareceria embaixo de uma célula
     * escrita R$ 500 mil e a gaveta perderia exatamente a credibilidade que
     * existe para dar.
     */
    /** Rotulo da coluna clicada, para a gaveta dizer o que esta detalhando. */
    columnLabel: {
      type: String,
      default: null,
    },
    /**
     * Tipo da coluna clicada: 'money', 'int', 'percent', 'decimal'.
     *
     * Quatro das sete colunas que abrem gaveta sao CONTAGEM. Sem o tipo, o
     * valor da celula na rede saia formatado como moeda e a celula "78" abria
     * dizendo "R$ 78,00".
     */
    columnType: {
      type: String,
      default: null,
    },
    networkAmount: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      nomes: {},
    }
  },
  computed: {
    gruposDeCliente() {
      return groupByCustomer(this.items)
    },
    /**
     * Quantas colunas a linha do cliente atravessa entre ele e o valor.
     *
     * Solução + data, mais status quando a leitura vem de contratos. Sem isto a
     * linha do cliente desalinha da linha da solução, e a tabela deixa de ler
     * como uma coisa só.
     */
    colunasDoMeio() {
      return this.meta.source === 'contracts' ? 2 : 1
    },
    rotuloBucket() {
      return ROTULOS_DE_BUCKET[this.meta.bucket] || 'Detalhamento'
    },
    tituloAcessivel() {
      return `${this.rotuloBucket} de ${this.consultantName}`
    },
    periodoLegivel() {
      if (!this.meta.start_date) {
        return ''
      }
      const inicio = this.$options.filters.onlyDate(this.meta.start_date)
      const fim = this.$options.filters.onlyDate(this.meta.end_date)
      return inicio === fim ? inicio : `${inicio} a ${fim}`
    },
    diferenca() {
      return Math.abs((this.meta.cell_amount || 0) - (this.meta.items_amount || 0))
    },
    /**
     * O valor da célula na unidade DELA: contagem como número, dinheiro como
     * moeda. `toCurrency` em tudo fazia "EP 1" virar "R$ 1,00".
     */
    valorDaCelula() {
      if (this.meta.unidade === 'contagem') {
        return String(Math.round(Number(this.meta.cell_amount) || 0))
      }

      return this.$options.filters.toCurrency(this.meta.cell_amount)
    },
    /**
     * A coluna do dono só existe quando dono e executor podem ser pessoas
     * diferentes — hoje, na perspectiva de especialista. Mostrá-la sempre
     * repetiria o nome da linha em toda célula e ensinaria a ignorá-la.
     */
    /**
     * O valor da celula na rede, formatado pelo TIPO da coluna clicada.
     *
     * `toCurrency` em tudo fazia contagem virar dinheiro. Percentual e decimal
     * tambem passam por aqui porque nada impede uma coluna dessas ganhar
     * `detailBucket` amanha.
     */
    valorDaRede() {
      if (this.networkAmount === null || this.networkAmount === undefined) {
        return ''
      }

      if (this.columnType === 'money') {
        return this.$options.filters.toCurrency(this.networkAmount)
      }

      if (this.columnType === 'percent') {
        return this.$options.filters.toPercentage(this.networkAmount)
      }

      if (this.columnType === 'decimal') {
        return this.$options.filters.toDecimal(this.networkAmount)
      }

      return String(this.networkAmount)
    },
    mostraDono() {
      return this.items.some(item => item.owner_consultant_id)
    },
  },
  watch: {
    items: {
      immediate: true,
      handler() {
        this.resolverNomes()
      },
    },
    /**
     * Marca no `body` enquanto a gaveta está aberta.
     *
     * Os botões de suporte do App.vue (`.float-bottom-left`, "Atendimento
     * online" e "Feedback de erro") são `position: fixed` no canto inferior
     * esquerdo com `z-index: 100000`, e a gaveta é uma folha ancorada embaixo:
     * eles caíam EM CIMA da primeira coluna dela, escondendo o nome do cliente
     * de três ou quatro linhas. Visto na tela; nenhum teste pegaria.
     *
     * A primeira solução foi subir os botões para cima da gaveta — e eles
     * paravam em cima do MENU lateral, tapando os itens do meio dele. A de
     * agora ataca a causa: a gaveta começa na borda do conteúdo, e não embaixo
     * do menu. Com o menu aberto os botões ficam onde sempre estiveram, sobre o
     * pé do menu, e não encostam na gaveta. Quando não há essa folga (menu
     * recolhido, tela menor que xl) eles somem enquanto a gaveta está aberta —
     * o precedente é o WhatsNewModal, que faz o mesmo com `body.whats-new-open`.
     */
    visible: {
      immediate: true,
      handler(aberta) {
        document.body.classList.toggle('kpi-drawer-open', !!aberta)
      },
    },
  },
  /**
   * Esc fecha a gaveta.
   *
   * Ela se declara `role="dialog"` e não escutava tecla nenhuma — para sair era
   * preciso achar o X com o mouse. Achado olhando a tela; teste de unidade não
   * pega porque não há nada para chamar.
   *
   * O listener é no document e não no elemento: a gaveta não recebe foco ao
   * abrir (`aria-modal="false"`, de propósito — a tabela continua usável atrás
   * dela), então um handler local só funcionaria depois de um clique dentro.
   */
  mounted() {
    document.addEventListener('keydown', this.aoTeclar)
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.aoTeclar)
    document.body.classList.remove('kpi-drawer-open')
  },
  methods: {
    aoTeclar(evento) {
      if (this.visible && (evento.key === 'Escape' || evento.key === 'Esc')) {
        this.$emit('close')
      }
    },
    nomeDoConsultor(consultantId) {
      return this.consultantNames[consultantId] || `Consultor ${consultantId}`
    },
    nomeDoCliente(customerId) {
      return this.nomes[customerId] || nomesConhecidos.get(customerId) || `Cliente ${customerId}`
    },
    resolverNomes() {
      const pendentes = [...new Set(
        this.items
          .map(item => item.customer_id)
          .filter(id => id && !nomesConhecidos.has(id)),
      )]

      if (!pendentes.length) {
        return
      }

      axios
        .get('/api/customers/names', { params: { ids: pendentes } })
        .then(({ data }) => {
          const encontrados = {}
          const recebidos = data || []
          recebidos.forEach(customer => {
            nomesConhecidos.set(customer.id, customer.name)
            encontrados[customer.id] = customer.name
          })
          // Objeto novo: o Vue 2 não observa chave adicionada num objeto existente.
          this.nomes = { ...this.nomes, ...encontrados }
        })
        .catch(() => {
          // Sem nome a gaveta ainda serve: valor, solução e contrato estão lá,
          // e o id continua clicável. Derrubar a gaveta por causa do rótulo
          // seria trocar um problema pequeno por um grande.
        })
    },
    abrirCliente(customerId) {
      const destino = this.$router.resolve({ name: 'customer-profile', params: { id: customerId } })
      window.open(destino.href, '_blank')
    },
    abrirContrato(contractId) {
      const destino = this.$router.resolve({
        name: 'contract-details-summary',
        params: { id: contractId },
      })
      window.open(destino.href, '_blank')
    },
  },
}
</script>

<!-- Nao escopado de proposito: alcanca os botoes fixos do App.vue e o
     conteudo do layout. -->
<style lang="scss">
// Sem folga ao lado da gaveta, os botoes somem enquanto ela esta aberta.
body.kpi-drawer-open .float-bottom-left {
  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition: opacity 160ms ease, visibility 0s linear 160ms;
}

// Com o menu aberto (so existe em xl) os botoes ficam sobre o pe do menu, e a
// gaveta comeca depois dele: podem continuar onde estao. O layout e irmao dos
// botoes dentro do #app, dai o `~`.
body.kpi-drawer-open .vertical-layout.vertical-menu-modern.menu-expanded ~ .float-bottom-left {
  opacity: 1;
  visibility: visible;
  pointer-events: auto;
  transition: none;
}

// A pagina ganha folga embaixo do tamanho da gaveta: sem ela, a ultima linha
// da tabela nao tinha como rolar para cima dela, e ficava coberta pelo proprio
// detalhamento que abriu.
body.kpi-drawer-open .app-content {
  padding-bottom: 52vh;
}
</style>

<style lang="scss" scoped>
.kpi-drawer {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1040;
  max-height: 52vh;
  display: flex;
  flex-direction: column;
  background: var(--kpi-drawer-bg, #fff);
  border-top: 1px solid var(--kpi-line-strong, rgba(34, 41, 47, 0.1));
  box-shadow: 0 -8px 32px var(--kpi-drawer-shadow, rgba(34, 41, 47, 0.16));
}

/* A gaveta comeca na borda do CONTEUDO, e nao embaixo do menu: ela detalha a
   tabela, e o menu continua usavel com ela aberta. As larguras sao as do
   template ($menu-expanded-width / $menu-collapsed-width), e as classes so
   existem em xl -- abaixo disso o menu e sobreposto e a gaveta vai de ponta a
   ponta. */
.vertical-menu-modern.menu-expanded .kpi-drawer {
  left: 260px;
}

.vertical-menu-modern.menu-collapsed .kpi-drawer {
  left: 80px;
}

.kpi-drawer__head {
  display: flex;
  align-items: flex-start;
  gap: 2rem;
  padding: 1rem 1.5rem 0.75rem;
  flex-wrap: wrap;
}

.kpi-drawer__bucket {
  display: block;
  font-size: 0.714rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--kpi-ink-muted, #b9b9c3);
}

.kpi-drawer__name {
  margin: 0.15rem 0 0;
}

.kpi-drawer__period {
  font-size: 0.786rem;
  color: var(--kpi-ink-muted, #b9b9c3);
}

.kpi-drawer__figures {
  display: flex;
  gap: 2rem;
  margin-left: auto;
  margin-right: 2.5rem;
}

.kpi-drawer__figure-label {
  display: block;
  font-size: 0.714rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--kpi-ink-muted, #b9b9c3);
}

.kpi-drawer__figure--muted {
  color: var(--kpi-ink-muted, #b9b9c3);
  font-weight: 500;
}

.kpi-drawer__figure-value {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.kpi-drawer__ok {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  padding: 0.1rem 0.5rem;
  border-radius: 1rem;
  font-size: 0.72rem;
  font-weight: 600;
  background: var(--kpi-surface-accent, rgba(40, 199, 111, 0.12));
  color: var(--kpi-positive, #197b44);
}

.kpi-drawer__close {
  position: absolute;
  top: 0.75rem;
  right: 1rem;
}

.kpi-drawer__warning {
  display: flex;
  align-items: center;
  margin: 0 1.5rem 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.35rem;
  font-size: 0.786rem;
  background: rgba(255, 159, 67, 0.12);
  color: #ff9f43;
}

.kpi-drawer__body {
  overflow: auto;
  padding: 0 1.5rem 1rem;
}

.kpi-drawer__table {
  width: 100%;
  font-size: 0.857rem;

  th {
    position: sticky;
    top: 0;
    background: var(--kpi-drawer-bg, #fff);
    padding: 0.4rem 0.5rem;
    font-size: 0.714rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--kpi-ink-muted, #b9b9c3);
    white-space: nowrap;
  }

  td {
    padding: 0.4rem 0.5rem;
    border-top: 1px solid var(--kpi-line, rgba(34, 41, 47, 0.06));
  }
}

.kpi-drawer__value {
  font-variant-numeric: tabular-nums;
}

.kpi-drawer__empty {
  padding: 2rem 0;
  text-align: center;
  color: var(--kpi-ink-muted, #b9b9c3);
}

.kpi-drawer__foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1.5rem 0.75rem;
  border-top: 1px solid var(--kpi-line, rgba(34, 41, 47, 0.06));
}

// A GAVETA EM DOIS NIVEIS
//
// O cliente e a ancora e a solucao e o detalhe, entao o peso visual segue essa
// ordem: a linha do cliente tem fundo e peso, a da solucao e clara e recuada.
// Sem a diferenca as duas leem como uma lista plana com buracos.
.kpi-drawer__client > td {
  background: var(--kpi-surface-sunken, #fbfbfd);
  border-top: 1px solid var(--kpi-line, #ebe9f1);
  font-weight: 600;
}

.kpi-drawer__solution > td {
  border-top: 0;
}

.kpi-drawer__sol-name {
  display: inline-block;
  padding-left: 1.25rem;
  position: relative;

  // O ponto marca 'isto pertence a linha de cima' sem precisar de linha guia,
  // que numa tabela de poucas linhas vira ruido.
  &::before {
    content: '';
    position: absolute;
    left: 0.4rem;
    top: 0.55em;
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--kpi-ink-faint, #d5d3de);
  }
}
.kpi-drawer__count {
  font-size: 0.786rem;
  color: var(--kpi-ink-muted, #b9b9c3);
}

/* Na linha do cliente a contagem vem colada no nome, e sem margem saia
   "Schwartz3 soluções". */
.kpi-drawer__client .kpi-drawer__count {
  margin-left: 0.6rem;
  font-weight: 400;
}

.kpi-drawer-enter-active,
.kpi-drawer-leave-active {
  transition: transform 220ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease;
}

.kpi-drawer-enter,
.kpi-drawer-leave-to {
  transform: translateY(16px);
  opacity: 0;
}

@media (prefers-reduced-motion: reduce) {
  .kpi-drawer-enter-active,
  .kpi-drawer-leave-active {
    transition: none;
  }

  .kpi-drawer-enter,
  .kpi-drawer-leave-to {
    transform: none;
  }
}

/* ---------------------------------------------------------------------------
   Tela estreita.

   A gaveta sobe mais alto (52% de uma tela de celular nao cabe uma tabela) e os
   numeros do cabecalho passam a empilhar, porque lado a lado eles ficariam com
   duas palavras por linha. A tabela rola na horizontal em vez de espremer: um
   valor em reais quebrado em duas linhas nao se le.
   --------------------------------------------------------------------------- */
@media (max-width: 767.98px) {
  .kpi-drawer {
    max-height: 78vh;
  }

  .kpi-drawer__head {
    gap: 0.75rem;
    padding: 0.85rem 1rem 0.5rem;
  }

  .kpi-drawer__figures {
    margin-left: 0;
    margin-right: 0;
    gap: 1.25rem;
    flex-wrap: wrap;
  }

  .kpi-drawer__warning {
    margin-left: 1rem;
    margin-right: 1rem;
  }

  .kpi-drawer__body {
    padding: 0 1rem 1rem;
    overflow-x: auto;
  }

  .kpi-drawer__table {
    min-width: 520px;
  }

  .kpi-drawer__foot {
    padding: 0.5rem 1rem 0.75rem;
  }
}
</style>
